import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Button from 'flarum/common/components/Button';

/**
 * (1) Tema: SOLO dos estados visibles — claro ↔ oscuro (T10).
 *
 * QUE ESTABA MAL. `fof/nightmode` cicla TRES estados para quien no tiene sesion
 * (`forum/helpers/switchTheme.ts::toggleThrough`: AUTO → CLARO → OSCURO → AUTO). Para el
 * usuario eso se ve como un boton que a veces "no hace nada": desde AUTO con el sistema en
 * claro, el primer clic lleva a CLARO — el mismo pixel en pantalla. El hallazgo ya estaba
 * anotado en el runbook §13.5 y §14.2 como trade-off; aqui se cierra.
 *
 * OJO: la rama de usuario CON sesion del propio `switchTheme()` ya es de dos estados
 * (`isLight ? DARK : LIGHT`). El defecto vive solo en la rama anonima / per-device. Aun asi
 * este modulo cubre las dos, porque el mismo boton sirve a las dos y partir la logica en dos
 * sitios es justo como se desincronizan estas cosas.
 *
 * COMO QUEDA:
 *   - Primera visita, sin preferencia -> manda `fof-nightmode.default_theme`. Eso es AJUSTE DE
 *     SETTING, no codigo: este modulo no lo toca ni lo asume.
 *
 *     OJO, ESE AJUSTE SE HA IDO SOLO UNA VEZ. T06-OPS lo puso en OSCURO (2) el 2026-08-05 y lo
 *     verifico (runbook §14.2); el 2026-08-07, leyendo el payload de produccion, volvia a valer
 *     0 (AUTO) — o sea, tema segun el sistema operativo del visitante, que es justo lo que se
 *     habia decidido no hacer. Como no vive en el repo, ningun test ni deploy lo defiende: si la
 *     landing vuelve a verse clara en frio, mirar ESTO antes que el codigo. Runbook §31.
 *   - Un clic -> guarda SIEMPRE un valor explicito (CLARO o OSCURO), nunca AUTO, y persiste
 *     igual que lo haria la extension (cookie `flarum_nightmode` o preferencia del usuario).
 *
 * POR QUE REEMPLAZAR EL ITEM Y NO PARCHEAR `switchTheme`. El `onclick` del boton de
 * `fof/nightmode` captura la funcion del modulo por referencia
 * (`addSettingsItems.js`: `onclick={() => { switchTheme(); }}`), asi que pisar el export
 * publico no cambiaria nada. Lo unico que se puede interceptar de verdad es el item.
 *
 * DEPENDENCIA EN RUNTIME, NO EN BUILD: se leen los helpers reales de la extension desde
 * `flarum.extensions['fof-nightmode']` (expuestos por su `forum/index.ts` y su
 * `common/index.ts`, verificado en vivo). Si `fof/nightmode` no estuviera instalado, el item
 * no existe y esta funcion no hace nada.
 */

type NightMode = {
  Themes: { AUTO: number; LIGHT: number; DARK: number; DEFAULT: () => number };
  getTheme: () => number;
  perDeviceSetting: { set: (value: number) => void };
  setSelectedTheme: { setTheme: () => void };
};

function nightMode(): NightMode | null {
  const ext = (window as any).flarum?.extensions?.['fof-nightmode'];

  // Se comprueba pieza por pieza: una version futura que deje de exportar cualquiera de
  // estas rompe el toggle en silencio si aqui se asume que estan.
  if (!ext?.Themes || !ext?.getTheme || !ext?.perDeviceSetting?.set || !ext?.setSelectedTheme?.setTheme) {
    return null;
  }

  return ext as NightMode;
}

/**
 * ¿El tema EFECTIVO ahora mismo es el claro? Misma definicion que
 * `fof/nightmode` (`switchTheme.ts::getIsLight`): AUTO se resuelve contra el sistema.
 */
function isLightNow(nm: NightMode): boolean {
  const theme = nm.getTheme();

  if (theme === nm.Themes.LIGHT) return true;
  if (theme === nm.Themes.DARK) return false;

  return !window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Guarda un tema EXPLICITO y repinta. Copia deliberada de las dos ramas de persistencia de
 * `switchTheme()` — cookie por dispositivo vs. preferencia del usuario — para no cambiar
 * donde vive el dato, solo que valor se guarda.
 */
function setExplicitTheme(nm: NightMode, value: number) {
  const user = app.session.user;

  if (user && !user.preferences()?.fofNightMode_perDevice) {
    // OJO CON EL ORDEN. La version de T10 llamaba a `setTheme()` DENTRO del `.then()` del
    // guardado, asi que un `savePreferences` que fallara (red caida, 500, sesion expirada)
    // dejaba el estado incoherente: el icono ya habia conmutado —lo pinta el redraw, no la
    // promesa— y el tema no. El usuario ve un boton que "no hace nada".
    //
    // Se aplica primero y se persiste despues. `savePreferences` ya actualiza el modelo local
    // en memoria de forma optimista, asi que `getTheme()` devuelve el valor nuevo aunque el
    // POST siga en vuelo, y `setTheme()` no necesita esperarlo.
    //
    // Encontrado al diagnosticar el objetivo 5 en el banco, donde el POST moria por CORS
    // (Flarum sirve `localhost:8888` y la sonda entraba por `[::1]:8888`). El origen estaba
    // mal en la sonda, pero el modo de fallo que destapo es real en produccion.
    user.savePreferences({ fofNightMode: value });

    // `setTheme()` a mano: un redraw de Mithril no cuenta como carga de pagina y la
    // extension solo recalcula el <link> en `Page.oninit`.
    nm.setSelectedTheme.setTheme();

    return;
  }

  nm.perDeviceSetting.set(value);
  nm.setSelectedTheme.setTheme();
}

/**
 * Conmuta al tema CONTRARIO al que esta puesto AHORA MISMO.
 *
 * QUE ESTABA MAL (BUG-2, PKG-FORO-BUGS-POLISH). El `onclick` se construia con el `light` que
 * se habia calculado arriba, en `HeaderSecondary.items()`. Ese valor solo se refresca cuando
 * mithril vuelve a dibujar, y mithril dibuja dentro de un `requestAnimationFrame`. En un
 * navegador que NO compone —pestaña de fondo, ventana tapada, throttling agresivo, un cliente
 * automatizado sin compositor— ese callback no llega nunca: el boton se queda con el `light`
 * del primer render y CADA clic posterior vuelve a guardar EL MISMO valor. El sintoma medido
 * es exactamente ese: el primer clic cambia el tema y escribe la cookie, y del segundo en
 * adelante la cookie queda congelada y la hoja no se mueve, hasta recargar.
 *
 * El arreglo no es forzar el redibujado —no se puede garantizar—, es no depender de el: el
 * destino se decide LEYENDO el estado vigente en el momento del clic. `getTheme()` no cachea
 * (`fof/nightmode` lo resuelve contra la cookie o las preferencias en cada llamada), asi que
 * aqui siempre se lee lo que hay.
 *
 * El `m.redraw()` es aparte y es solo cosmetico: repinta icono, `aria-label` y
 * `data-cm-theme` para que el control se vea como el tema que acaba de quedar. Si el
 * navegador tampoco dibuja, el TEMA sigue conmutando bien; lo unico que se queda atras es el
 * dibujo del boton, en un entorno donde no se dibuja nada.
 */
function toggleTheme(nm: NightMode) {
  const light = isLightNow(nm);

  setExplicitTheme(nm, light ? nm.Themes.DARK : nm.Themes.LIGHT);

  m.redraw();
}

export default function themeToggle() {
  extend(HeaderSecondary.prototype, 'items', function (items: any) {
    const nm = nightMode();

    if (!nm) return;

    // CON SESION EL ITEM NO EXISTE, Y ESE ERA EL BUG (T12 · objetivo 5).
    //
    // `fof/nightmode` abre su propio `HeaderSecondary.items` con un `return` seco:
    //
    //   if (app.session.user && !app.forum.attribute('fofNightMode.showThemeToggleOnHeaderAlways')) return;
    //
    // o sea que a quien tiene sesion NO le pone boton en la cabecera — lo manda al
    // `SessionDropdown` (el menu del avatar), donde David no lo encontraba. T10 se apoyaba en
    // `items.has('nightmode')` para reemplazarlo, asi que logueado no habia nada que reemplazar
    // y el control simplemente desaparecia.
    //
    // Se arregla en CODIGO y no con el ajuste `showThemeToggleOnHeaderAlways`: un setting no
    // viaja con el deploy, no esta en ningun test, y el dia que alguien lo resetee en el panel
    // el toggle se vuelve a esfumar sin que nada lo delate.
    //
    // `replace` si ya esta / `add` si no: nunca duplica. Con el ajuste en true el item existe y
    // se reemplaza (comportamiento de T10, intacto); con el ajuste en false o con sesion, se
    // anade. La prioridad 15 es la misma que usa la extension, para no mover el orden.
    const present = items.has('nightmode');

    // Solo para PINTAR el boton (icono y `data-cm-theme`). La decision de a que tema lleva el
    // clic NO sale de aqui: la toma `toggleTheme()` releyendo el estado vigente. Ver su
    // cabecera — atar el manejador a este valor es lo que producia BUG-2.
    const light = isLightNow(nm);
    const iconPrefix = app.forum.attribute('fofNightMode.showThemeToggleInSolid') ? 'fas fa-' : 'far fa-';

    const button = (
      <Button
        className="Button Button--flat cm-theme-toggle"
        data-cm-theme={light ? 'light' : 'dark'}
        aria-label={app.translator.trans('fof-nightmode.forum.header.nightmode_button')}
        onclick={() => toggleTheme(nm)}
        icon={iconPrefix + (light ? 'sun' : 'moon')}
      >
        {app.translator.trans('fof-nightmode.forum.header.nightmode_button')}
      </Button>
    );

    if (present) {
      items.replace('nightmode', button);
    } else {
      items.add('nightmode', button, 15);
    }
  });
}
